import { Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { defer, forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { EmployeeWorkExperience, EmployeeWorkExperienceVerbose } from './models/employee-work-experience.model';
import { EmployeeWorkExperienceService } from './services/employee-work-experience.service';
import { api_routes, db_tables } from '@app/consts';
import { FormGeneratorDialogComponent } from '@app/shared/components/form-generator-dialog/form-generator-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ActivatedRoute } from '@angular/router';
import { EmployeeTableSecurity } from '@app/shared/models/employee.model';
import { TablePermissionsService } from '@app/core/services/table-permissions/table-permissions.service';

@Component({
  selector: 'app-employee-work-experience',
  templateUrl: './employee-work-experience.component.html',
  styleUrls: ['./employee-work-experience.component.scss']
})
export class EmployeeWorkExperienceComponent implements OnInit {
  @Input() formIds: string[];
  employeeID: string;

  public columns: any[] = [
    { field: "jobTitle", title: "job Title", type: "navigation", tableId: "tfi_WeJobTitle"},
    { field: "department", title: "department", tableId: "tfi_WeDepartment"},
    { field: "workLocation", title: "work Location", tableId: "tfi_WeWorkLocation"},
    { field: "company", title: "company", tableId: "tfi_WeCompany"},
    { field: "manager", title: "manager", tableId: "tfi_WeManager"},
    { field: "startDate", title: "start Date", type: "date", tableId: "tfi_WeStartDate"},
    { field: "endDate", title: "end date", type: "date", tableId: "tfi_WeEndDate"},
    { field: "udt1", title: "udt1", tableId: "tfi_WeUdt1"},
    { field: "udt2", title: "udt2", tableId: "tfi_WeUdt2"},
    { field: "udt3", title: "udt3", tableId: "tfi_WeUdt3"},
    { field: "udt4", title: "udt4", tableId: "tfi_WeUdt4"},
  ];
  public bindingType: String = "array";
  public view: any;
  public gridData: any;
  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean;
  pageSize: number = 5;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  dialogRef: MatDialogRef<FormGeneratorDialogComponent>;
  formId: string = 'frm_pIddWWLTy6JZ5t'
  changeReasonFormId: string = 'frm_PXkjOIui9XoKWv';
  formValid: boolean;
  sortString: string = 'startDate-desc';
  isLoadingPermissions: boolean = true;
  targetEmployeeTablePermissions: EmployeeTableSecurity[];
  accessAndHistoryURL: string = `${api_routes.TALENT}/[employeeId]/${api_routes.WORK_EXPERIENCES}/[entityId]`;

  constructor(
    private dialog: MatDialog,
    private translate: TranslateService,
    private employeeWorkExperienceService: EmployeeWorkExperienceService,
    private snackbarService: SnackbarService,
    private overlayService: OverlayService,
    private tablePermissionsService: TablePermissionsService,
    private route: ActivatedRoute,
  ) {
    this.route.paramMap.subscribe(
        params => this.employeeID = params.get("id")
    )
  }

  ngOnInit(): void {
    this.view = this.getWorkExperiences();
    this.getTargetEmployeeTablePermissions();
  }

  getWorkExperiences() {
    this.isLoading = true;

    this.employeeWorkExperienceService.getWorkExperiences(this.employeeID, this.pageSize, this.skip, this.sortString)
      .pipe(
        finalize( () => {
          this.isLoading = false;
        })
      )
      .subscribe(
        (res) => {
          this.gridDataResult = {
            data: res.data,
            total: res.total,
          }
          this.gridData = res.data;
        }
      );
  }

  getWorkExperience(workExperience?: EmployeeWorkExperience) {
    if(workExperience === undefined){
      this.openFormDialog();
    }
    else {
      this.isLoading = true;

      this.employeeWorkExperienceService.getWorkExperience(this.employeeID, workExperience.id)
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe(
        res => {
          this.openFormDialog(res);
        }
      );
    }
  }

  getTargetEmployeeTablePermissions() {
    this.isLoadingPermissions = true;

    let currentFetchedData = this.tablePermissionsService.getFetchedTargetEmployeeTablePermissions(this.employeeID);

    // if the current table permissions that are saved are for this employee use those, else fetch the new permissions.
    if(currentFetchedData !== null){
      this.targetEmployeeTablePermissions = currentFetchedData;
      this.isLoadingPermissions = false;
    }
    else {
      this.tablePermissionsService.getTargetEmployeeTablePermissions(this.employeeID)
      .pipe(
        finalize(() => {
            this.isLoadingPermissions = false;
        })
      )
      .subscribe(
        res => {
          this.targetEmployeeTablePermissions = res;
        }
      );
    }
  }

  openFormDialog(workExperience?: EmployeeWorkExperienceVerbose) {
    let formData = {
      id: workExperience ? workExperience.id : null,
      asOf: moment().format(),
      changeReason: '',
      changeReasonComments: '',
      jobTitle: workExperience ? workExperience.jobTitle : null,
      department: workExperience ? workExperience.department : null,
      workLocation: workExperience ? workExperience.workLocation : null,
      company: workExperience ? workExperience.company : null,
      startDate: workExperience ? workExperience.startDate : null,
      endDate: workExperience ? workExperience.endDate : null,
      manager: workExperience ? workExperience.manager : null,
      udt1: workExperience ? workExperience.udt1 : null,
      udt2: workExperience ? workExperience.udt2 : null,
      udt3: workExperience ? workExperience.udt3 : null,
      udt4: workExperience ? workExperience.udt4 : null,
    };

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      formTitle: `${this.translate.instant('WorkHistory')}`,
      formId: this.formId,
      changeReasonFormId: this.changeReasonFormId,
      targetEmployeeTablePermissions: this.targetEmployeeTablePermissions,
      formData: formData
    };

    this.dialogRef = this.dialog.open(FormGeneratorDialogComponent, dialogConfig);

    const sub = this.dialogRef.componentInstance.emitFormData.subscribe((event) => {
      this.save(event)
    });
  }

  formDataEmitted(formData) {
    this.save(formData);
  }

  formStatusUpdated(formValid) {
    this.formValid = formValid;
  }

  save(formData: any){
    this.overlayService.show();

    if (formData.startDate) {formData.startDate = moment(formData.startDate).format().slice(0, 10);}
    if (formData.endDate) {formData.endDate = moment(formData.endDate).format().slice(0, 10);}

    if(!formData.id){
      this.employeeWorkExperienceService.createWorkExperience(this.employeeID, formData)
      .pipe(
        finalize(() => this.overlayService.hide())
      )
      .subscribe(
        (res) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('CreatedSuccessfully')}`, 'clear', 'success');
          this.dialogRef.close(true);
          this.getWorkExperiences();
        }
      );
    }
    else {
      this.employeeWorkExperienceService.updateWorkExperience(this.employeeID, formData)
      .pipe(
        finalize(() => this.overlayService.hide())
      )
      .subscribe(
        (res) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
          this.dialogRef.close(true);
          this.getWorkExperiences();
        }
      );
    }
  }

  deleteWorkExperiences(workExperienceIds: string[]) {
    const observables = workExperienceIds.map(selectedItem => defer(() => this.employeeWorkExperienceService.deleteWorkExperience(this.employeeID, selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
    .pipe(
      finalize(() => this.overlayService.hide())
    )
    .subscribe(
      (res) => {
        this.view = this.getWorkExperiences();
        this.clearSelectedItems = !this.clearSelectedItems;
          this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
      }
    );
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take
    this.view = this.getWorkExperiences();
  }

}
